import { base64Encode } from "@openanalyst-ai/core/util/encode"
import { Button } from "@openanalyst-ai/ui/button"
import { useDialog } from "@openanalyst-ai/ui/context/dialog"
import { Dialog } from "@openanalyst-ai/ui/dialog"
import { IconButton } from "@openanalyst-ai/ui/icon-button"
import { Mark } from "@openanalyst-ai/ui/logo"
import { Markdown } from "@openanalyst-ai/ui/markdown"
import type { Part } from "@openanalyst-ai/sdk/v2/client"
import { useNavigate } from "@solidjs/router"
import { createEffect, createMemo, createSignal, For, onCleanup, Show } from "solid-js"
import { firstRunFollowupFailedMessage, firstRunStartFailedMessage } from "@/components/error-copy"
import { getOpenAnalystAccountModelFromToken } from "@/context/auth/token"
import { useGlobalSDK } from "@/context/global-sdk"
import { useLocalOptional } from "@/context/local"
import { useModels } from "@/context/models"
import {
  firstRunPromptForTemplate,
  starterPanelItemForTemplate,
  type ProjectStarterTemplateID,
} from "@/services/project-starters"
import { Identifier } from "@/utils/id"
import { resolveFirstRunModel, type FirstRunModelRef } from "./onboarding-first-run-model"
import "./onboarding-first-run-dialog.css"

type Phase = "intro" | "running" | "done" | "error"

function activityLabel(part: Extract<Part, { type: "tool" }>) {
  if (part.tool === "skill") return "Loaded the workspace guide"
  if (part.tool === "memory") return "Checked for existing business context"
  if (part.tool === "read" || part.tool === "glob" || part.tool === "grep") return "Checked the project state"
  if (part.tool === "webfetch" || part.tool === "websearch") return "Reviewed public business information"
  if (part.tool === "write" || part.tool === "edit" || part.tool === "write_chunk") return "Updated workspace records"
  if (part.tool === "todowrite") return "Updated the setup plan"
  return "Worked on the guided setup"
}

/** Opens after the welcome dialog and keeps the real first-run session focused
 * on one decision at a time. The full technical trace remains available in chat.
 */
export function OnboardingFirstRunDialog(props: {
  templateId: ProjectStarterTemplateID | null
  directory: string
}) {
  const dialog = useDialog()
  const navigate = useNavigate()
  const globalSDK = useGlobalSDK()
  // Mounted from Layout via dialog.show(), i.e. outside LocalProvider — optional.
  const local = useLocalOptional()
  const models = useModels()

  const starter = createMemo(() => (props.templateId ? starterPanelItemForTemplate(props.templateId) : null))
  const prompt = createMemo(() => firstRunPromptForTemplate(props.templateId))

  const [phase, setPhase] = createSignal<Phase>("intro")
  const [error, setError] = createSignal<string | null>(null)
  const [sessionID, setSessionID] = createSignal<string | null>(null)
  const [parts, setParts] = createSignal<Part[]>([])
  let curatedMessageID = ""
  const [followupIDs, setFollowupIDs] = createSignal<Set<string>>(new Set())
  const [draft, setDraft] = createSignal("")
  const [selectedModel, setSelectedModel] = createSignal<FirstRunModelRef>()
  const [runModel, setRunModel] = createSignal<FirstRunModelRef>()
  const [runModelName, setRunModelName] = createSignal("")
  let modelChosen = false
  let unsubscribe: (() => void) | undefined
  onCleanup(() => unsubscribe?.())
  const isUserPart = (p: Part) => followupIDs().has(p.messageID)

  const availableModels = createMemo(() =>
    models
      .list()
      .filter((model) => models.visible({ providerID: model.provider.id, modelID: model.id }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  )
  createEffect(() => {
    if (modelChosen || selectedModel()) return
    const account = getOpenAnalystAccountModelFromToken()
    const current = local?.model.current()
    setSelectedModel(
      resolveFirstRunModel({
        recent: [
          ...(current ? [{ providerID: current.provider.id, modelID: current.id }] : []),
          ...models.recent.list(),
        ],
        account: account ? { providerID: account.providerID, modelID: account.modelID } : undefined,
        available: availableModels().map((model) => ({ providerID: model.provider.id, modelID: model.id })),
      }),
    )
  })

  const selectedModelInfo = createMemo(() => {
    const selected = selectedModel()
    if (!selected) return
    return models.find(selected)
  })
  const activeModel = () => runModel() ?? selectedModel()
  const activeModelValue = () => {
    const selected = activeModel()
    return selected ? `${selected.providerID}/${selected.modelID}` : ""
  }
  const chooseModel = (value: string) => {
    const slash = value.indexOf("/")
    if (slash <= 0) return
    const model = { providerID: value.slice(0, slash), modelID: value.slice(slash + 1) }
    modelChosen = true
    setSelectedModel(model)
    if (phase() !== "intro") {
      setRunModel(model)
      setRunModelName(models.find(model)?.name ?? `${model.providerID}/${model.modelID}`)
    }
    models.recent.push(model)
  }

  const upsertPart = (part: Part) => {
    setParts((prev) => {
      const idx = prev.findIndex((p) => p.id === part.id)
      if (idx === -1) return [...prev, part]
      const next = prev.slice()
      next[idx] = part
      return next
    })
  }

  // The guided view shows only the conversation. Internal reasoning and tool payloads
  // remain in the full chat, while this surface presents a calm progress summary.
  const conversationParts = createMemo(() =>
    parts().filter((p) => {
      if (p.messageID === curatedMessageID) return false
      if (p.type !== "text") return false
      return !(p as { synthetic?: boolean; ignored?: boolean }).synthetic && !(p as { ignored?: boolean }).ignored
    }),
  )
  const activities = createMemo(() => [
    ...new Set(parts().filter((part): part is Extract<Part, { type: "tool" }> => part.type === "tool").map(activityLabel)),
  ])
  const userAnswers = createMemo(() => conversationParts().filter(isUserPart))
  const latestAssistantPart = createMemo(() => conversationParts().findLast((part) => !isUserPart(part)))
  const latestAssistantText = createMemo(() => (latestAssistantPart() as { text?: string } | undefined)?.text ?? "")
  const questionProgress = createMemo(() => {
    const match = latestAssistantText().match(/question\s+(\d+)\s+of\s+(\d+)/i)
    if (!match) return
    return { current: Number(match[1]), total: Number(match[2]) }
  })
  const activeStep = createMemo(() => {
    if (phase() === "intro") return 0
    if (latestAssistantText().toLowerCase().includes("plan_review")) return 3
    if (userAnswers().length < 4) return 1
    return 2
  })

  const working = () => phase() === "running"

  const start = async () => {
    const model = selectedModel()
    if (!model) return
    setRunModel(model)
    setRunModelName(selectedModelInfo()?.name ?? `${model.providerID}/${model.modelID}`)
    models.recent.push(model)
    setError(null)
    setParts([])
    setPhase("running")
    try {
      const created = await globalSDK.client.session.create({ directory: props.directory, title: "First run" })
      const id = created.data?.id
      if (!id) throw new Error("Could not create the first-run session.")
      setSessionID(id)
      unsubscribe = globalSDK.event.listen((event) => {
        const d = event.details
        if (d.type === "message.part.updated") {
          const part = d.properties.part
          if (part.sessionID !== id) return
          upsertPart(part)
          return
        }
        if (d.type === "session.idle") {
          const idleProps = d.properties as { sessionID?: string; info?: { id?: string } }
          if ((idleProps.sessionID ?? idleProps.info?.id) === id && phase() === "running") setPhase("done")
        }
      })
      void globalSDK.event.start()
      curatedMessageID = Identifier.ascending("message")
      await globalSDK.client.session.promptAsync({
        directory: props.directory,
        sessionID: id,
        messageID: curatedMessageID,
        model,
        variant: model ? models.variant.get(model) : undefined,
        parts: [{ id: Identifier.ascending("part"), type: "text", text: prompt() }],
      })
    } catch (err) {
      console.error("[onboarding:first-run] start failed", err)
      setError(firstRunStartFailedMessage())
      setPhase("error")
    }
  }

  // Interactive follow-up: when the agent asks for a minimal input (a store URL,
  // an API key/token, a quick answer), the user types it here and we send it to
  // the SAME session — so the run continues end-to-end inside the dialog without
  // detouring to Settings/Integrations.
  const sendFollowup = async (text: string) => {
    const trimmed = text.trim()
    const id = sessionID()
    if (!trimmed || !id) return
    setDraft("")
    setPhase("running")
    const msgID = Identifier.ascending("message")
    setFollowupIDs((prev) => new Set(prev).add(msgID))
    try {
      const model = runModel() ?? selectedModel()
      await globalSDK.client.session.promptAsync({
        directory: props.directory,
        sessionID: id,
        messageID: msgID,
        model,
        variant: model ? models.variant.get(model) : undefined,
        parts: [{ id: Identifier.ascending("part"), type: "text", text: trimmed }],
      })
    } catch (err) {
      console.error("[onboarding:first-run] follow-up send failed", err)
      setError(firstRunFollowupFailedMessage())
    }
  }

  const continueToChat = () => {
    const id = sessionID()
    dialog.close()
    if (id) navigate(`/${base64Encode(props.directory)}/session/${id}`, { replace: true })
    else navigate(`/${base64Encode(props.directory)}/session`, { replace: true })
  }

  const skipToChat = () => {
    dialog.close()
    navigate(`/${base64Encode(props.directory)}/session`, { replace: true })
  }

  return (
    <Dialog fit transition class="oa-firstrun-dialog">
      <div class="oa-firstrun-shell">
        <header class="oa-firstrun-head">
          <div class="oa-firstrun-logo">
            <Mark class="oa-firstrun-logo-mark" />
          </div>
          <div class="oa-firstrun-head-copy">
            <h2 class="oa-firstrun-title">
              Set up <span class="oa-firstrun-title-accent">{starter()?.title ?? "your project"}</span>
            </h2>
            <p class="oa-firstrun-subtitle">
              <Show
                when={phase() === "intro"}
                fallback="Your submitted answers stay with this project. Continue in chat whenever you want more room."
              >
                A few focused answers, then an evidence-backed plan for you to review.
              </Show>
            </p>
          </div>
          <Show when={phase() === "intro"}>
            <IconButton
              type="button"
              icon="close"
              variant="ghost"
              aria-label="Close"
              class="oa-firstrun-close"
              onClick={() => skipToChat()}
            />
          </Show>
        </header>

        <div class="oa-firstrun-body">
          <div class="oa-firstrun-runbar">
            <div class="oa-firstrun-model-field">
              <span>Run with</span>
              <select
                aria-label="Model for first run"
                value={activeModelValue()}
                disabled={working()}
                onChange={(event) => chooseModel(event.currentTarget.value)}
              >
                <Show when={!activeModel()}>
                  <option value="">Choose a model</option>
                </Show>
                <For each={availableModels()}>
                  {(model) => <option value={`${model.provider.id}/${model.id}`}>{model.name}</option>}
                </For>
              </select>
            </div>
            <span class="oa-firstrun-model-note">
              {phase() === "intro"
                ? selectedModelInfo()
                  ? "Review-first"
                  : "Model required"
                : working()
                  ? `Using ${runModelName() || "selected model"}`
                  : "Changes apply to the next answer"}
            </span>
          </div>

          <ol class="oa-firstrun-steps" aria-label="Workspace setup progress">
            <For each={["Confirm", "Understand", "Build", "Review"]}>
              {(label, index) => (
                <li data-state={index() < activeStep() ? "done" : index() === activeStep() ? "active" : "pending"}>
                  <span class="oa-firstrun-step-dot">{index() < activeStep() ? "✓" : index() + 1}</span>
                  <span>{label}</span>
                </li>
              )}
            </For>
          </ol>

          <Show when={phase() === "intro"}>
            <div class="oa-firstrun-intro-card">
              <div>
                <div class="oa-firstrun-intro-kicker">What this run will produce</div>
                <h3>A grounded diagnostic and an editable first-week plan.</h3>
                <p>The assistant asks only for missing context. Integrations stay optional.</p>
              </div>
              <div class="oa-firstrun-safety-note">
                Nothing is scheduled, published, sent, or handed to Symphony without a separate approval.
              </div>
            </div>
          </Show>

          <Show when={phase() !== "intro"}>
            <div class="oa-firstrun-workspace">
              <section class="oa-firstrun-question-card" aria-live="polite">
                <div class="oa-firstrun-question-meta">
                  <span>
                    {questionProgress()
                      ? `Question ${questionProgress()?.current} of ${questionProgress()?.total}`
                      : activeStep() === 3
                        ? "Ready for review"
                        : activeStep() === 2
                          ? "Building your plan"
                          : working()
                            ? userAnswers().length
                              ? "Preparing the next question"
                              : "Preparing the first question"
                            : `Question ${userAnswers().length + 1}`}
                  </span>
                  <Show when={working()}>
                    <span class="oa-firstrun-processing">
                      <span class="oa-pk-spinner" aria-hidden="true" /> Working
                    </span>
                  </Show>
                </div>
                <Show
                  when={latestAssistantPart()}
                  fallback={
                    <div class="oa-firstrun-focus-card">
                      <span class="oa-pk-spinner" aria-hidden="true" />
                      <div>
                        <strong>Preparing your workspace</strong>
                        <span>Checking the durable project state before asking the first question.</span>
                      </div>
                    </div>
                  }
                >
                  {(part) => (
                    <div class="oa-firstrun-question-copy">
                      <Markdown text={(part() as { text?: string }).text ?? ""} />
                    </div>
                  )}
                </Show>
              </section>

              <Show when={userAnswers().length > 0}>
                <details class="oa-firstrun-answers">
                  <summary>
                    <span>{userAnswers().length} {userAnswers().length === 1 ? "answer" : "answers"} saved</span>
                    <span>Review answers</span>
                  </summary>
                  <div class="oa-firstrun-answer-list">
                    <For each={userAnswers()}>
                      {(part, index) => (
                        <div class="oa-firstrun-answer-row">
                          <span>{index() + 1}</span>
                          <p>{(part as { text?: string }).text ?? ""}</p>
                        </div>
                      )}
                    </For>
                  </div>
                </details>
              </Show>

              <Show when={activities().length > 0}>
                <details class="oa-firstrun-activity">
                  <summary>{activities().length} setup checks completed</summary>
                  <ul>
                    <For each={activities()}>{(activity) => <li>{activity}</li>}</For>
                  </ul>
                </details>
              </Show>
            </div>
          </Show>

          <Show when={error()}>
            <div class="oa-firstrun-error">{error()}</div>
          </Show>

        </div>

        <Show when={phase() === "running" || phase() === "done"}>
          <div class="oa-firstrun-answer-dock">
            <form
              class="oa-firstrun-composer"
              onSubmit={(e) => {
                e.preventDefault()
                void sendFollowup(draft())
              }}
            >
              <textarea
                class="oa-firstrun-input"
                rows={2}
                placeholder={working() ? "You can draft your next answer while OpenAnalyst works…" : "Type your answer…"}
                value={draft()}
                onInput={(e) => setDraft(e.currentTarget.value)}
                autocomplete="off"
              />
              <Button type="submit" variant="primary" size="normal" disabled={working() || !draft().trim()}>
                {working() ? "Please wait" : "Send answer"}
              </Button>
            </form>
            <div class="oa-firstrun-answer-dock-meta">
              <span>Your draft stays here if you click outside.</span>
              <Button type="button" variant="ghost" size="normal" disabled={!!draft().trim()} onClick={() => continueToChat()}>
                Continue in chat
              </Button>
            </div>
          </div>
        </Show>

        <footer class="oa-firstrun-actions">
          <Show when={phase() === "intro"}>
            <Button type="button" variant="secondary" size="large" onClick={() => skipToChat()}>
              Not now
            </Button>
            <Button type="button" variant="primary" size="large" disabled={!selectedModel()} onClick={() => void start()}>
              Begin setup
            </Button>
          </Show>
          <Show when={phase() === "error"}>
            <Button type="button" variant="secondary" size="large" onClick={() => skipToChat()}>
              Go to chat
            </Button>
            <Button type="button" variant="primary" size="large" onClick={() => void start()}>
              Try again
            </Button>
          </Show>
        </footer>
      </div>
    </Dialog>
  )
}
